import React, { useState, useEffect } from "react";
import privateAxios from "../../../utils/axiosPrivate";
import { Link, useParams } from "react-router-dom";
import { Table } from "reactstrap";
import { customer_status } from "../../../utils/data/data";
import {
  downloadFileFromResponse,
  formatDateWithDayjs,
  sanitizeInput,
  showToast
} from "../../../utils/utils";
import styles from "./../../../assets/scss/custom.module.scss";
import "./event.scss";
import {
  buttonName,
  commonText,
  customerLogType,
  customersDtls,
  customerTab,
  fileDownloadMessages,
  noDataPresent,
  responseStatus
} from "../../../locales/en";
import {
  getAcceptedConsentDownloadUrl,
  getActivitySearchUrl
} from "../../../utils/constants/apiEndPoints";
import TableSkeleton from "../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../hooks/useVisibleColumnCount";
import SearchField from "../../common/searchbox/SearchField";
import {
  ACCEPTEDCREDITCONSENT,
  COLUMN_COUNT_2,
  MIN_LOGS_TO_SHOW,
  PAGE_INDEX,
  PAGE_SIZE,
  PAGE_SIZE_50
} from "../../../utils/constants/appConstants";

const CustomerEventLog = () => {
  const { id } = useParams();
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();
  const [showDetails, setShowDetails] = useState({});
  const [showDescription, setShowDescription] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [logTypes, setLogTypes] = useState("");
  const [allLogs, setallLogs] = useState([]);
  const [page, setPage] = useState(PAGE_INDEX);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    setPage(PAGE_INDEX);
    setallLogs([]);
    setHasMore(true);
  }, [searchQuery, logTypes, id]);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        if (page === PAGE_INDEX) setLoading(true);
        else setIsFetching(true);

        const response = await privateAxios.get(getActivitySearchUrl, {
          params: {
            userAccountId: id,
            keyword: sanitizeInput(searchQuery),
            logTypes: logTypes,
            page: page,
            size: PAGE_SIZE_50
          }
        });

        const responseData = response.data?.data || [];

        if (page === PAGE_INDEX) {
          setallLogs(responseData);
        } else {
          setallLogs((prev) => {
            const newIds = responseData.map((log) => log.id);
            const filteredPrev = prev.filter(
              (log) => log.id === undefined || !newIds.includes(log.id)
            );
            return [...filteredPrev, ...responseData];
          });
        }

        if (response.data?.pageInfo) {
          setHasMore(page < response.data.pageInfo.totalPages - 1);
        } else {
          setHasMore(responseData.length === PAGE_SIZE);
        }
      } catch {
        if (page === PAGE_INDEX) setallLogs([]);
      } finally {
        setLoading(false);
        setIsFetching(false);
      }
    };
    fetchLogs();
  }, [searchQuery, id, logTypes, page]);

  function toggleShow(index) {
    setShowDetails((prevState) => ({
      ...prevState,
      [index]: !prevState[index]
    }));
  }

  const toggleDescription = () => {
    setShowDescription((prev) => {
      const newState = !prev;
      setTimeout(() => {
        const scrollContainer = document.getElementById("hitScroll");
        if (scrollContainer) {
          scrollContainer.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        }
      }, 0);
      return newState;
    });
  };

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + PAGE_SIZE &&
      hasMore &&
      !isFetching &&
      !loading
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  // Handle changes in the search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
    if (value.trim() === "") {
      setSearchQuery("");
    }
  };

  // Handle key press events in the search input
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const trimmedQuery = tempSearchQuery.trim();
      setSearchQuery(trimmedQuery);
    }
  };

  // Handle key press events in the search input
  const handleLogTypeChange = (event) => {
    setLogTypes(event.target.value);
  };

  const handleConsentDownload = async () => {
    try {
      const response = await privateAxios.get(
        getAcceptedConsentDownloadUrl(id),
        {
          responseType: "blob"
        }
      );
      downloadFileFromResponse(response);
      showToast(
        fileDownloadMessages?.errors?.downloadSuccess,
        responseStatus?.success
      );
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors ||
        error.response?.data?.message ||
        fileDownloadMessages?.errors?.downloadFailed;
      showToast(errorMessage, responseStatus?.error);
    }
  };

  return (
    <>
      <div className="blog-input-wrap mb-5">
        <div className="row d-flex flex-wrap justify-content-sm-between">
          <div className="col-12 col-sm-6 col-md-4">
            <select
              className={`${styles.selectdropdown}`}
              onChange={handleLogTypeChange}
              value={logTypes}
              disabled={loading}
            >
              {customer_status.map((customerlog, index) => (
                <option key={index} value={customerlog.value}>
                  {customerlog.name}
                </option>
              ))}
            </select>
          </div>
          <div className="d-flex  col-sm-6 justify-content-sm-end search">
            <SearchField
              tempSearchQuery={tempSearchQuery}
              handleSearchChange={handleSearchChange}
              handleKeyDown={handleKeyDown}
              loading={loading}
            />
          </div>
        </div>
      </div>
      <div
        className={`event-section customer_event ${showDescription ? "event-sec-wrap" : ""}`}
        id="hitScroll"
        onScroll={handleScroll}
      >
        <Table
          bordered
          className={`${styles.tableForall} header-bg-primary event-table`}
        >
          <thead ref={headerRef}>
            <tr>
              <th>{customerTab?.customerLog.evenTextt}</th>
            </tr>
          </thead>
          {loading ? (
            <tbody>
              <TableSkeleton rows={5} columns={visibleColumnCount} />
            </tbody>
          ) : (
            <tbody>
              {allLogs.length === 0 ? (
                <tr>
                  <td colSpan={COLUMN_COUNT_2} className="text-center">
                    {noDataPresent}
                  </td>
                </tr>
              ) : (
                allLogs.map((event, index) => (
                  <tr key={index}>
                    <td>
                      <div className="content">
                        <div className="mb-2">
                          <strong>
                            {event.title ? event.title : event.description}
                          </strong>
                        </div>

                        {event.logType === ACCEPTEDCREDITCONSENT && (
                          <div className="content-desc mb-1">
                            <Link
                              className="link-primary"
                              onClick={handleConsentDownload}
                            >
                              {customerLogType?.consentAccept}
                            </Link>
                          </div>
                        )}

                        <div className="content-desc mb-2">
                          <button
                            className="show-desc-content"
                            onClick={() => toggleShow(index)}
                          >
                            {showDetails[index]
                              ? "Hide Details"
                              : "Show Details"}
                          </button>
                          {showDetails[index] && (
                            <div className="description-details">
                              {event.title ? (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeInput(event.description)
                                  }}
                                />
                              ) : (
                                ""
                              )}
                            </div>
                          )}
                        </div>

                        <small>
                          {`${customersDtls.noteCmmnt.on} `}
                          {formatDateWithDayjs(event.createdAt)}
                        </small>
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {isFetching && (
                <tr>
                  <td colSpan={COLUMN_COUNT_2} className="text-center">
                    {commonText.fallbackLoader}
                  </td>
                </tr>
              )}
            </tbody>
          )}
        </Table>
      </div>
      {!loading &&
        Array.isArray(allLogs) &&
        allLogs.length > MIN_LOGS_TO_SHOW && (
          <button
            className="show-desc-content mt-2"
            onClick={toggleDescription}
          >
            {showDescription ? buttonName.seeLess : buttonName.seeMore}
          </button>
        )}
    </>
  );
};

export default CustomerEventLog;
